import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Routes whose last path segment is the loan number. An explicit list rather than "any numeric tail"
 * because plenty of other screens end in an id too (/ai-projects/3, /pool/12) and telling the
 * assistant it is inside loan 3 when it is not is worse than telling it nothing.
 *
 * A loan route missing from this list simply gets no loan context - the assistant asks which loan,
 * the way it did before - so this failing behind the router is a degradation, not a defect.
 */
const LOAN_ROUTE_PREFIXES = new Set([
  'amortization_schedule',
  'appraisal_information',
  'bytepro_logs',
  'closingDisclosure1',
  'closingDisclosureAddendum',
  'closing_calendar',
  'closing_screen',
  'creditDenial',
  'dataverify',
  'document_genie',
  'expiration_dates',
  'fci',
  'fee_worksheet',
  'hmda',
  'lightningDocs',
  'automateLoanDocs',
  'loanEstimate',
  'loanEstimate2',
  'loanEstimate3',
  'loanInTake',
  'loan_ai_project',
  'loan_change_request',
  'loan_process',
  'national_flood',
  'notes',
  'order_valuation',
  'post_closing',
  'purchase_advice',
  'purchase_advice_seller',
  'reo',
  'review_form',
  'secondary_marketing_screen',
  'servicing',
  'underwriting',
  'underwriting_summary',
])

/**
 * The loan the user currently has open, or 0. Read from the URL rather than from Api.getLoanNumber(),
 * which keeps its last value after you leave a loan - that stale value is exactly what would make
 * the assistant answer about the wrong loan on a portfolio screen.
 */
export function useActiveLoanNumber(): number {
  const { pathname } = useLocation()

  return useMemo(() => {
    const segments = pathname.split('/').filter(Boolean)
    if (segments.length < 2 || !LOAN_ROUTE_PREFIXES.has(segments[0])) return 0
    const loanNumber = Number(segments[segments.length - 1])
    return Number.isInteger(loanNumber) && loanNumber > 0 ? loanNumber : 0
  }, [pathname])
}
